import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import API_BASE from '../config';

export default function ClientManagement() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filterCountry, setFilterCountry] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', countryId: '' });
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = user?.role === 'SUPERADMIN';

  const loadCountries = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await fetch(`${API_BASE}/admin/countries`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (res.ok) setCountries(data.countries || []);
    } catch {}
  }, [isSuperAdmin]);

  const loadClients = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = filterCountry ? `?countryId=${filterCountry}` : '';
      const res = await fetch(`${API_BASE}/admin/clients${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load clients');
      setClients(data.clients || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filterCountry]);

  useEffect(() => { loadCountries(); }, [loadCountries]);
  useEffect(() => { loadClients(); }, [loadClients]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', countryId: isSuperAdmin ? (filterCountry || '') : (user?.countryId?.toString() || '') });
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, countryId: c.countryId?.toString() || '' });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(''); setSuccess('');
    try {
      const url = editing ? `${API_BASE}/admin/clients/${editing.id}` : `${API_BASE}/admin/clients`;
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ name: form.name, countryId: parseInt(form.countryId) })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess(editing ? 'Client updated.' : 'Client created.');
      setShowForm(false);
      loadClients();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete client "${name}"?`)) return;
    setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_BASE}/admin/clients/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess('Client deleted.');
      loadClients();
    } catch (e) {
      setError(e.message);
    }
  };

  const countryName = (id) => countries.find(c => c.id === id)?.name || '—';
  const countryCode = (id) => countries.find(c => c.id === id)?.code || '';

  return (
    <div style={s.page}>
      <div style={s.container}>
        <div style={s.header}>
          <div>
            <button onClick={() => navigate('/admin')} className="btn-back-nav" style={{ marginBottom: 8 }}>← Back</button>
          <h1 style={s.title}>🏢 Client Management</h1>
            <p style={s.sub}>Manage clients within countries</p>
          </div>
          <button style={s.btnPrimary} onClick={openCreate}>+ Add Client</button>
        </div>

        {error   && <div style={s.alertErr}>{error}</div>}
        {success && <div style={s.alertOk}>{success}</div>}

        {isSuperAdmin && countries.length > 0 && (
          <div style={{ marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
            <label style={{ color: '#374151', fontWeight: 600, fontSize: '0.85rem' }}>Filter by country:</label>
            <select style={s.select} value={filterCountry} onChange={e => setFilterCountry(e.target.value)}>
              <option value="">All countries</option>
              {countries.map(c => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
            </select>
          </div>
        )}

        {showForm && (
          <div style={s.card}>
            <h3 style={{ margin: '0 0 16px', color: '#1e293b' }}>{editing ? 'Edit Client' : 'New Client'}</h3>
            <form onSubmit={handleSubmit}>
              <div style={s.grid2}>
                <div>
                  <label style={s.label}>Client Name *</label>
                  <input style={s.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Acme Corp" required />
                </div>
                {isSuperAdmin && (
                  <div>
                    <label style={s.label}>Country *</label>
                    <select style={s.select} value={form.countryId} onChange={e => setForm(f => ({ ...f, countryId: e.target.value }))} required>
                      <option value="">— Select country —</option>
                      {countries.map(c => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button style={s.btnPrimary} type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                <button style={s.btnGhost} type="button" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div style={s.center}>Loading…</div>
        ) : clients.length === 0 ? (
          <div style={s.empty}>No clients found. Click "Add Client" to create one.</div>
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Client Name</th>
                  {isSuperAdmin && <th style={s.th}>Country</th>}
                  <th style={s.th}>Users</th>
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map(c => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={s.td}><strong>{c.name}</strong></td>
                    {isSuperAdmin && (
                      <td style={s.td}>
                        <span style={s.badge}>{countryCode(c.countryId)}</span>
                        <span style={{ marginLeft: 6, color: '#374151' }}>{countryName(c.countryId)}</span>
                      </td>
                    )}
                    <td style={s.td}>{c._count?.users || 0}</td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button style={s.btnSm} onClick={() => openEdit(c)}>Edit</button>
                        <button style={{ ...s.btnSm, ...s.btnDanger }} onClick={() => handleDelete(c.id, c.name)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#f8fafc', padding: '32px 16px' },
  container: { maxWidth: 1000, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  title: { margin: 0, fontSize: '1.6rem', color: '#1e293b', fontWeight: 700 },
  sub: { margin: '4px 0 0', color: '#64748b', fontSize: '0.9rem' },
  card: { background: '#fff', borderRadius: 10, padding: 24, marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', border: '1px solid #e2e8f0' },
  alertErr: { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 16px', borderRadius: 8, marginBottom: 16 },
  alertOk: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', padding: '10px 16px', borderRadius: 8, marginBottom: 16 },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  label: { display: 'block', marginBottom: 6, color: '#374151', fontWeight: 600, fontSize: '0.85rem' },
  input: { width: '100%', padding: '9px 12px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: '0.9rem', boxSizing: 'border-box' },
  select: { padding: '9px 12px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: '0.9rem', background: '#fff', minWidth: 180 },
  tableWrap: { background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', border: '1px solid #e2e8f0' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 16px', background: '#f8fafc', color: '#374151', fontWeight: 700, fontSize: '0.85rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '12px 16px', color: '#374151', fontSize: '0.9rem', verticalAlign: 'middle' },
  badge: { display: 'inline-block', background: '#dbeafe', color: '#1e40af', borderRadius: 5, padding: '1px 8px', fontSize: '0.78rem', fontWeight: 700 },
  btnPrimary: { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 7, padding: '9px 20px', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' },
  btnGhost: { background: '#f1f5f9', color: '#374151', border: '1px solid #d1d5db', borderRadius: 7, padding: '9px 20px', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' },
  btnSm: { background: '#f1f5f9', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, padding: '5px 12px', fontSize: '0.8rem', cursor: 'pointer' },
  btnDanger: { background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5' },
  center: { textAlign: 'center', padding: 40, color: '#94a3b8' },
  empty: { background: '#fff', borderRadius: 10, padding: 40, textAlign: 'center', color: '#64748b', border: '1px solid #e2e8f0' }
};
